import { useTheme } from '@mui/material';
import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiFetch } from '../utilities/api';
import { constructMediaUrl } from '../utilities/mediaUtils';
import { generatePopupContent } from '../utilities/generatePopupContent';
import { useConfig } from '../contexts/ConfigContext';
import { registerContextCleanupHandler, unregisterContextCleanupHandler } from './UserContext';
import { MapLayerConfig } from '../types';

// Types for popup
interface PopupProperties {
  [key: string]: any;
}

// Define a type for the layer config that only includes what we need for popups
interface PopupLayerConfig {
  style?: { 
    labelField?: string;
  };
  popupFields?: string[];
  descriptionField?: string;
}

interface CurrentPopupContextProps {
  popupContent: string | null;
  popupCoords: { lng: string; lat: string } | null;
  setPopupContent: (properties: PopupProperties, layerId?: string) => Promise<string>;
  setPopupCoords: (coords: { lng: string; lat: string }) => void;
  clearPopup: () => void;
  clearAllSessionState: () => void;
}

// Context Creation
const CurrentPopupContext = createContext<CurrentPopupContextProps | undefined>(
  undefined
);

// Context Provider Component
export const CurrentPopupProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // State Management
  const [popupContent, setPopupContentState] = useState<string | null>(null);
  const [popupCoords, setPopupCoords] = useState<{
    lng: string;
    lat: string;
  } | null>(null);
  const theme = useTheme();
  const config = useConfig();

  // Popup Clear Function
  const clearPopup = () => {
    setPopupContentState(null);
    setPopupCoords(null);
  };

  // Clear all session state for logout
  const clearAllSessionState = () => {
    console.log('[PopupContext] Clearing all session state for logout');
    setPopupContentState(null);
    setPopupCoords(null);
    console.log('[PopupContext] Session state cleared successfully');
  };

  // Register cleanup handler
  useEffect(() => {
    registerContextCleanupHandler(clearAllSessionState);
    return () => unregisterContextCleanupHandler(clearAllSessionState);
  }, [clearAllSessionState]);

  /**
   * Helper function to find a suitable image from media items when no primary image is set
   * @param placeId - The ID of the place to find an image for
   * @returns URL of the first suitable image found, or undefined if none found
   */
  const inferPrimaryImageWhenNotDefined = async (placeId: number): Promise<string | undefined> => {
    try {
      const mediaEndpoint = config.apiEndpoints.find(e => e.entityType === 'media')?.api_endpoint;
      const mediaData = await apiFetch(
        `${mediaEndpoint}?place=${placeId}`
      );
      
      const firstDisplayableImage = mediaData.find(
        (item: any) => {
          // Check if it's an image type
          const isImageType = item.media_type?.startsWith('image/');
          // Check if we have a thumbnail
          const hasThumbnail = !!item.thumbnail;
          
          return isImageType && hasThumbnail;
        }
      );
      
      if (firstDisplayableImage?.thumbnail) {
        return constructMediaUrl(firstDisplayableImage.thumbnail);
      }
      
      return undefined;
    } catch (error) {
      return undefined;
    }
  };

  // Main Popup Content Setting Function
  const setPopupContent = async (properties: PopupProperties, layerId?: string) => {
    let updatedProperties = { ...properties };
    
    // Find the layer configuration if layerId is provided
    let layerConfig: PopupLayerConfig | undefined;
    if (layerId) {
      const fullLayerConfig = config.mapLayers.find(layer => layer.id === layerId);
      
      // Extract only the properties we need for popup generation
      if (fullLayerConfig) {
        // Create an empty layer config
        layerConfig = {};
        
        // Only add style if it exists (GeoJSON and Fulcrum layers have it)
        if ('style' in fullLayerConfig) {
          layerConfig.style = fullLayerConfig.style;
        }
        
        // Add popup fields if they exist
        if ('popupFields' in fullLayerConfig) {
          layerConfig.popupFields = fullLayerConfig.popupFields;
        }
        
        // Add description field if it exists
        if ('descriptionField' in fullLayerConfig) {
          layerConfig.descriptionField = fullLayerConfig.descriptionField;
        }
      }
    }
    
    if (properties.id) {
      try {
        const placesEndpoint = config.apiEndpoints.find(e => e.name === 'Places')?.api_endpoint;
        const placeDetails = await apiFetch(
          `${placesEndpoint}${properties.id}/`
        );
        
        // If we have a thumbnail, use it and we're done
        if (placeDetails.thumbnail) {
          updatedProperties.image_url = placeDetails.thumbnail;
          
        // Otherwise try primary image
        } else if (placeDetails.primary_image?.url) {
          try {
            const response = await fetch(placeDetails.primary_image.url);
            if (response.ok) {
              updatedProperties.image_url = placeDetails.primary_image.url;
            }
          } catch (error) {}
          
        // Last resort - try to infer an image
        } else {
          const inferredImageUrl = await inferPrimaryImageWhenNotDefined(properties.id);
          if (inferredImageUrl) {
            updatedProperties.image_url = inferredImageUrl;
          }
        }
      } catch (error) {
        console.error('Error fetching details for Cultural Site:', error);
      }
    }

    // Pass the layer configuration to the popup generator
    const generatedHtml = generatePopupContent(
      updatedProperties, 
      theme, 
      { layerConfig }
    );
    
    setPopupContentState(generatedHtml);
    return generatedHtml;
  };

  // Context Provider Return
  return (
    <CurrentPopupContext.Provider
      value={{
        popupContent,
        popupCoords,
        setPopupContent,
        setPopupCoords,
        clearPopup,
        clearAllSessionState,
      }}
    >
      {children}
    </CurrentPopupContext.Provider>
  );
};

// Custom Hook for Using the Popup Context
export const useCurrentPopup = () => {
  const context = useContext(CurrentPopupContext);
  if (context === undefined) {
    throw new Error(
      "useCurrentPopup must be used within a CurrentPopupProvider"
    );
  }
  return context;
};
